import React, { useState, useEffect } from 'react';
import { Phone, Mail, Target, Trophy, Pencil } from 'lucide-react';
import api from '../lib/api';
import { formatCurrency } from '../mock';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const inputCls = 'w-full bg-muted/40 border border-border rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors';

const GoalDialog = ({ member, month, open, onClose, onDone }) => {
  const [form, setForm] = useState({ type: 'count', target: '' });
  useEffect(() => {
    if (open) setForm({ type: member?.goal?.type || 'count', target: member?.goal?.target || '' });
  }, [open, member]);
  const save = async () => {
    try {
      await api.put('/team-goals', { user_id: member.user_id, month, type: form.type, target: Number(form.target) });
      toast({ title: '🎯 Meta definida', description: `${member.name} · ${month}` });
      onClose(); onDone();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-sm">
        <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Meta de {month}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">Funcionário: <span className="text-foreground font-semibold">{member?.name}</span></p>
        <div className="space-y-3.5">
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Tipo de meta</label>
            <select data-testid="goal-type-select" className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <option value="count" className="bg-popover">Por vendas feitas (quantidade)</option>
              <option value="value" className="bg-popover">Por valor vendido ($)</option>
            </select>
          </div>
          <div><label className="text-xs text-muted-foreground mb-1.5 block">{form.type === 'count' ? 'Quantidade de vendas' : 'Valor em $'}</label>
            <input data-testid="goal-target-input" type="number" min="1" className={inputCls} value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} placeholder={form.type === 'count' ? 'Ex: 8' : 'Ex: 150000'} />
          </div>
          <p className="text-[11px] text-muted-foreground/70">A venda é contabilizada quando o deal do vendedor atinge <span className="text-teal-400 font-semibold">Registration Completed</span>.</p>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground">Cancelar</button>
          <button data-testid="goal-save-btn" onClick={save} disabled={!form.target || Number(form.target) <= 0} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 disabled:opacity-50">Salvar Meta</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Equipe = () => {
  const { user } = useAuth();
  const [data, setData] = useState({ month: '', team: [] });
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [goalFor, setGoalFor] = useState(null);

  const canSetGoal = ['Administrator', 'Owner'].includes(user?.role);

  const load = async () => {
    try { const r = await api.get(`/team-overview?month=${month}`); setData(r.data); } catch (e) {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const isSales = (m) => ['Salesperson', 'Sales Manager'].includes(m.role);
  const initials = (n) => (n || '?').split(' ').slice(0, 2).map((x) => x[0]).join('').toUpperCase();

  return (
    <div className="space-y-6" data-testid="equipe-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">{data.team.length} funcionários cadastrados · metas contabilizam vendas em Registration Completed</p>
        <input data-testid="equipe-month" type="month" className="bg-muted/40 border border-border rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors" value={month} onChange={(e) => setMonth(e.target.value)} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {data.team.map((m, idx) => (
          <div key={m.user_id} data-testid={`equipe-card-${m.login || m.user_id}`} className="rounded-xl border border-border/70 bg-card p-5 hover:border-border transition-colors">
            <div className="flex items-start gap-3">
              <div className="relative">
                <span className="w-12 h-12 rounded-full bg-primary/15 text-primary flex items-center justify-center text-sm font-bold">{initials(m.name)}</span>
                {idx === 0 && m.sales_value > 0 && (<span className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-primary flex items-center justify-center"><Trophy className="w-3 h-3 text-foreground" /></span>)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold">{m.name}</p>
                <p className="text-xs text-muted-foreground">{m.job_title || m.role}</p>
              </div>
              <span className="text-[10px] font-semibold px-2 py-1 rounded-full text-muted-foreground bg-muted/50">{m.role}</span>
            </div>

            <div className="flex flex-col gap-1.5 mt-4 text-xs text-muted-foreground">
              {m.phone && <a href={`tel:${m.phone}`} className="flex items-center gap-1.5 hover:text-foreground transition-colors"><Phone className="w-3.5 h-3.5" />{m.phone}</a>}
              {m.email && <a href={`mailto:${m.email}`} className="flex items-center gap-1.5 hover:text-foreground transition-colors"><Mail className="w-3.5 h-3.5" />{m.email}</a>}
              {!m.phone && !m.email && <p className="text-muted-foreground/50">Sem contato cadastrado</p>}
            </div>

            <div className="mt-4 pt-4 border-t border-border/70">
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="flex items-center gap-1.5 text-muted-foreground"><Target className="w-3.5 h-3.5" /> Meta de {data.month}</span>
                {canSetGoal && <button data-testid={`set-goal-${m.login || m.user_id}`} onClick={() => setGoalFor(m)} className="flex items-center gap-1 text-[11px] font-bold text-primary hover:underline"><Pencil className="w-3 h-3" /> {m.goal ? 'Editar' : 'Definir'}</button>}
              </div>
              {m.goal ? (
                <>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="font-semibold" data-testid={`goal-progress-${m.login || m.user_id}`}>
                      {m.goal.type === 'count' ? `${m.sales_count}/${Math.round(m.goal.target)} vendas` : `${formatCurrency(m.sales_value)} / ${formatCurrency(m.goal.target)}`}
                    </span>
                    <span className={`font-bold ${m.progress_pct >= 100 ? 'text-emerald-400' : 'text-muted-foreground'}`}>{m.progress_pct ?? 0}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted/50 overflow-hidden"><div className={`h-full rounded-full transition-all ${m.progress_pct >= 100 ? 'bg-emerald-500' : 'bg-gradient-to-r from-primary/70 to-primary'}`} style={{ width: `${m.progress_pct ?? 0}%` }} /></div>
                </>
              ) : (
                <p className="text-[11px] text-muted-foreground/60">{isSales(m) ? 'Nenhuma meta definida para este mês.' : 'Sem meta (não é vendedor).'}</p>
              )}
              {isSales(m) && (
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-muted-foreground">Vendido no mês ({m.sales_count})</span>
                  <span className="font-display font-black text-primary">{formatCurrency(m.sales_value)}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <GoalDialog member={goalFor} month={month} open={!!goalFor} onClose={() => setGoalFor(null)} onDone={load} />
    </div>
  );
};

export default Equipe;
